import { usePrivy, useWallets } from "@privy-io/react-auth";
import { USDC_ADDRESS } from "env";
import React, { createContext, useContext, useEffect, useState } from "react";
import { ethers } from "ethers";
import USDC_ABI from "../contracts/usdc.json";
import PrinxContext from "contexts/prinxDataContext";
import { determineAddress, determineTest } from "common";
import { AssetType } from "types";
import { setUserHasDeposited, recordUserCompanyBalancesAsSnapshot } from "calls";
import { getPriceOfTokenByQuantity } from "./helpers/share-price-helpers";
export type QuotedCompanyValue = {
  title: string;
  quote: string;
};

interface BlockChainContextType {
  provider: ethers.providers.ExternalProvider | null;
  ethersProvider: ethers.providers.Web3Provider | null;
  usdcBalance: string;
  setUsdcBalance: React.Dispatch<React.SetStateAction<string>>;
  usdcBalanceLoaded: boolean;
  setUsdcBalanceLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  smartWalletAddress: string;
  companyBalances: { [key: string]: string };
  companyBalancesLoaded: boolean;
  setCompanyBalances: React.Dispatch<React.SetStateAction<any>>;
  fetchAndSetBalances: (recordBalances?: boolean) => Promise<void>;
  setSmartWalletAddress: React.Dispatch<React.SetStateAction<string>>;
  quotedCompanyValues: QuotedCompanyValue[];
}

const BlockChainContext = createContext<BlockChainContextType>({
  provider: null,
  ethersProvider: null,
  usdcBalance: "",
  setUsdcBalance: () => {},
  usdcBalanceLoaded: false,
  setUsdcBalanceLoaded: () => {},
  smartWalletAddress: "",
  companyBalances: {},
  companyBalancesLoaded: false,
  setCompanyBalances: () => {},
  fetchAndSetBalances: async () => {},
  setSmartWalletAddress: () => {},
  quotedCompanyValues: []
});

export const BlockChainContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { wallets } = useWallets();
  const { user } = usePrivy();
  const { assetData, prinxUser } = useContext(PrinxContext);
  const [provider, setProvider] = useState<ethers.providers.ExternalProvider | null>(null);
  const [ethersProvider, setEthersProvider] = useState<ethers.providers.Web3Provider | null>(null);
  const [smartWalletAddress, setSmartWalletAddress] = useState<string>("");
  const [usdcBalance, setUsdcBalance] = useState<string>("0");
  const [usdcBalanceLoaded, setUsdcBalanceLoaded] = useState(false);
  const [companyBalances, setCompanyBalances] = useState<{ [key: string]: string }>({});
  const [companyBalancesLoaded, setCompanyBalancesLoaded] = useState(false);
  const [quotedCompanyValues, setQuotedCompanyValues] = useState<QuotedCompanyValue[]>([]);

  useEffect(() => {
    if (user?.linkedAccounts && !smartWalletAddress) {
      const smartWallet = user.linkedAccounts.find((account) => account.type === "smart_wallet");
      if (smartWallet && "address" in smartWallet) {
        setSmartWalletAddress(smartWallet.address);
      }
    }
  }, [user]);

  useEffect(() => {
    const getEthersProvider = async () => {
      const wallet = wallets[0];
      if (wallet && smartWalletAddress) {
        console.log("Setting Provider, Ethers Provider");
        const ethereumProvider = await wallet.getEthereumProvider();
        setProvider(ethereumProvider);
        const ethersProv = new ethers.providers.Web3Provider(ethereumProvider);
        setEthersProvider(ethersProv);
      }
    };
    getEthersProvider();
  }, [wallets, smartWalletAddress]);

  // Extracted function renamed to getBalances

  const fetchAndSetBalances = async (recordBalances?: boolean) => {
    const zeroValueEntries = [];
    const wallet = wallets[0];
    if (wallet && smartWalletAddress && assetData) {
      if (!ethersProvider) return;
      // Get USDC balance
      const usdcContract = new ethers.Contract(USDC_ADDRESS, USDC_ABI, ethersProvider);
      const usdcBalanceRaw = await usdcContract.balanceOf(smartWalletAddress);

      if (prinxUser && !prinxUser.hasDeposited && ethers.utils.formatUnits(usdcBalanceRaw, 6).toString() !== "0.0") {
        console.log("Well We found ourselves setting hasDeposited", ethers.utils.formatUnits(usdcBalanceRaw, 6));
        setUserHasDeposited();
      }
      setUsdcBalance(ethers.utils.formatUnits(usdcBalanceRaw, 6));
      setUsdcBalanceLoaded(true);

      // Get token balances for each company
      const balances: { [key: string]: string } = {};
      const quoteTasks: Promise<{ title: string; quote: string } | null>[] = [];

      for (const company of assetData as AssetType[]) {
        const addy = determineAddress(company);
        if (company.abi && addy) {
          const contract = new ethers.Contract(addy, company.abi as ethers.ContractInterface, ethersProvider);
          const raw = await contract.balanceOf(smartWalletAddress);
          const formatted = ethers.utils.formatUnits(raw, 6);
          balances[company.title] = formatted;

          // If non-zero, prepare a quote task
          if (formatted !== "0.0") {
            quoteTasks.push(
              getPriceOfTokenByQuantity(company, formatted)
                .then((quote) => quote && { title: company.title, address: addy, quote, test: determineTest() })
                .catch((err) => {
                  console.error(`Failed to quote ${company.title}`, err);
                  return null;
                })
            );
          } else {
            // Explicitly add 0-value holding for this asset
            zeroValueEntries.push({
              title: company.title,
              address: addy,
              quote: "0.0",
              test: determineTest()
            });
          }
        }
      }
      setCompanyBalances(balances);
      setCompanyBalancesLoaded(true);
      const quoted = await Promise.all(quoteTasks);
      const valid = quoted.filter((q): q is { title: string; address: string; quote: string } => !!q);
      const finalSnapshotPayload = [...quoted, ...zeroValueEntries];

      if (recordBalances) {
        await recordUserCompanyBalancesAsSnapshot(finalSnapshotPayload);
      }
      setQuotedCompanyValues(valid);
    }
  };

  // Call getBalances whenever dependencies change
  useEffect(() => {
    if (!ethersProvider || !assetData || !prinxUser || (assetData && !assetData.length)) return;
    console.log("Fetching Balances First");
    fetchAndSetBalances();
    // getLiquidityForToken(ethersProvider, assetData[1].address);

    // Set up interval
    const intervalId = setInterval(() => {
      console.log("Fetching Balances");
      fetchAndSetBalances();
    }, 30000); // 30 seconds

    // Clean up on unmount or dependency change
    return () => clearInterval(intervalId);
  }, [ethersProvider, assetData, prinxUser]);

  const value = {
    provider,
    ethersProvider,
    usdcBalance,
    setUsdcBalance,
    usdcBalanceLoaded,
    setUsdcBalanceLoaded,
    companyBalances,
    companyBalancesLoaded,
    setCompanyBalances,
    smartWalletAddress,
    fetchAndSetBalances,
    setSmartWalletAddress,
    quotedCompanyValues
  };

  return <BlockChainContext.Provider value={value}>{children}</BlockChainContext.Provider>;
};

export default BlockChainContext;
